/* eslint-disable jsx-a11y/no-static-element-interactions */
// @ts-nocheck

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
// styles
import ComparisonCheckbox from '@jo-v1/components/CompareCheckbox';
import { type SchoolConfigData } from '@jo-v1/components/ExitStrategy/type';
import ProductFlags from '@jo-v2/components/ProductFlags';
import ProductTags from '@jo-v2/components/ProductTags';
import { useEffect, useState, useMemo, memo, type MouseEventHandler, useRef } from 'react';
import { FaStar } from 'react-icons/fa';
import { HiLocationMarker } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import useProductEvents from '@core/hooks/cohesion/useProductEvents';
// selectors, actions
import {
  selectRecommenderPopupListId,
  setHeclidMapEntry,
  setProductCorrelationIdMapEntry,
  setViewCorrelationIdMapEntry,
  selectHeclidMap,
  selectProductCorrelationIdMap,
} from '@core/reducers/eventingSlice';
import { selectAllInputs } from '@core/reducers/inputsSlice';
import {
  setLeadDelivery,
  selectLeadDelivery,
  setCurrentProgram,
  selectSubmissions,
  setShowDuplicatePopup,
} from '@core/reducers/matchesSlice';
import { mobiusGetApplicationCta, mobiusRedirect } from '@core/services/mobius';
import { getSchoolConfig } from '@core/services/schoolConfig';
import { VoyagerResult } from '@core/ts/results';
// hooks & utils
import cn from '@core/utils/classNames';
import getToken from '@core/utils/getToken';

import SchoolProgramTabs from '../SchoolProgramTabs';
import styles from './RecommenderCard.module.css';

type RecommenderCardProps = {
  result: VoyagerResult;
  setRecommenderStep?: (step: string) => void;
  position?: number;
  setLoading?: (loading: boolean) => void;
  eventingOverrides?: any;
  index?: number;
  withImage?: boolean;
  withTopBorder?: boolean;
  fullWidth?: boolean;
  onRecommender?: boolean;
};

const RecommenderCard = ({
  result,
  setRecommenderStep,
  position,
  setLoading,
  eventingOverrides,
  index,
  withImage = false,
  fullWidth = false,
  withTopBorder = true,
  onRecommender = true,
}: RecommenderCardProps) => {
  const { currentPrograms } = useSelector(selectLeadDelivery);

  // dispatches actions
  const dispatch = useDispatch();
  const cardRef = useRef<HTMLElement>(null);
  const [hasBeenViewed, setHasBeenViewed] = useState(false);

  // local state
  const [schoolConfig, setSchoolConfig] = useState<SchoolConfigData>({});
  const [applicationCtaData, setApplicationCtaData] = useState<{ label: string; url: string } | null>(null);

  // inputs state properties
  const inputs = useSelector(selectAllInputs);

  // Eventing Ids
  const popupListId = useSelector(selectRecommenderPopupListId);
  const newViewCorrelationId = useMemo(() => uuid(), []);

  // Store
  const heclidMap = useSelector(selectHeclidMap);
  const productCorrelationIdMap = useSelector(selectProductCorrelationIdMap);

  const flags = useFeatureFlags();
  const isComparisonTest = !!flags.programsComparisonTest;

  // Initialize events
  const { productLoaded, productViewed, productClicked } = useProductEvents({
    listId: popupListId,
    viewCorrelationId: newViewCorrelationId,
    product: {
      sku: String(result?.cap?.id),
      variant: result?.program?.degree?.slug,
      productId: String(result?.program?.id),
      name: result?.program?.subject?.slug,
      category: result?.program?.category?.slug,
      brand: result?.school?.slug,
      position,
      location: onRecommender ? 'voyager-recommender-pop-up' : eventingOverrides?.location,
      formatType: 'app',
      formatSubtype: 'jo-grid',
      positionEngine: 'algolia',
      ...eventingOverrides?.product,
    },
    customDimensions: eventingOverrides?.customDimensions,
  });

  // Fire productLoaded + productViewed events and adds entry to the viewCorrelationIdMap
  useEffect(() => {
    if (!cardRef.current || hasBeenViewed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !hasBeenViewed) {
          // The card is now in the viewport and hasn't been viewed yet
          productLoaded();
          productViewed();

          if (result?.program?.id) {
            dispatch(
              setViewCorrelationIdMapEntry({
                key: result.program.id,
                value: newViewCorrelationId,
              })
            );
          }

          // Mark as viewed to prevent duplicate events
          setHasBeenViewed(true);

          // Unobserve once viewed
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.5, // Fire when at least 50% of the card is visible
        rootMargin: '0px',
      }
    );

    observer.observe(cardRef.current);

    // eslint-disable-next-line consistent-return
    return () => {
      if (cardRef.current) observer.unobserve(cardRef.current);
    };
  }, [dispatch, result, productLoaded, productViewed, newViewCorrelationId, hasBeenViewed]);
  useEffect(() => {
    const getConfig = async () => {
      const schoolConfig = await getSchoolConfig(result?.school.id);
      setSchoolConfig(schoolConfig);
    };
    getConfig();
  }, [result]);

  const onBtnClickHandler = async (event) => {
    // if (!event.target.classList.contains('tab-trigger')) {
    setLoading(true);
    dispatch(setCurrentProgram(result));

    const newProductCorrelationId = uuid();

    // add correlationId to map for LDS lead submission
    if (result?.program?.id) {
      dispatch(
        setProductCorrelationIdMapEntry({
          key: result.program.id,
          value: newProductCorrelationId,
        })
      );
    }

    productClicked({
      correlationId: newProductCorrelationId,
    });

    // Mobius Redirect will fire the OutcomeTracked APP_VIEWED event
    const { heclid } = await mobiusRedirect({
      idToken: getToken(),
      productCorrelationId: newProductCorrelationId,
      link: result?.url,
      trackingContextOverride: {
        formatType: 'app',
        formatSubtype: 'jo-grid',
      },
    });
    // add heclid to map for Mobius track if API call succeeds
    if (result?.program?.id) {
      dispatch(setHeclidMapEntry({ key: result.program.id, value: heclid }));
    }

    setLoading(false);
    setRecommenderStep('PII_CONFIRMATION');
    // }
  };

  // Matches State properties
  const submissions = useSelector(selectSubmissions);
  const isProgramSubmitted = submissions.some((s) => s.program.id === result.program.id);
  const isSchoolSubmitted = !!submissions?.find((submission) => submission.school.id === result?.school.id);
  const handleProductClick = () => {
    // If school is already submitted, show duplicate popup
    if (isSchoolSubmitted) {
      dispatch(
        setLeadDelivery({
          show: true,
          currentPrograms: [result],
          showExitStep: true,
        })
      );
      dispatch(setShowDuplicatePopup(true));
      return;
    }

    // If program hasn't been submitted, show lead delivery slide-out
    const productCorrelationId = uuid();
    dispatch(setProductCorrelationIdMapEntry({ key: result.program.id, value: productCorrelationId }));
    productClicked({ correlationId: productCorrelationId });

    dispatch(
      setLeadDelivery({
        show: true,
        currentPrograms: [result],
        showExitStep: false,
      })
    );
  };

  useEffect(() => {
    const getApplicationCtaData = async () => {
      const response = await mobiusGetApplicationCta({
        programId: `${result.program.id}`,
        heclid: heclidMap[result.program.id],
        correlationId: productCorrelationIdMap[result.program.id],
      });
      setApplicationCtaData({ label: response?.label, url: response?.url });
    };

    getApplicationCtaData();
  }, []);

  const handleClick = () => {
    if (isProgramSubmitted && applicationCtaData?.url) {
      return window.open(applicationCtaData.url, '_blank');
    }
    if (onRecommender) {
      return onBtnClickHandler();
    }
    return handleProductClick();
  };

  return (
    <section
      ref={cardRef}
      className={cn(styles.card, fullWidth ? styles.fullWidthCard : undefined)}
      onClick={handleClick}
    >
      {withTopBorder && (
        <div className={styles.cardTopBorder}>
          <div className={styles.bestMatchWrapper}>
            {index === 0 && (
              <span className={styles.starIconContainer}>
                <FaStar size={11} color="#FFCC00" />
              </span>
            )}
            {index === 0 ? 'Best Match' : 'Great Match'}
          </div>
          <ProductTags decisionFactors={inputs?.['decision-factors']} />
        </div>
      )}
      {withImage && <img className={styles.heroImage} src={schoolConfig?.image} alt={result?.school.name} />}
      <div className={styles.bodyContainer}>
        <div className={styles.cardHeader}>
          <div className={styles.leftContent}>
            <div className={styles.logo}>
              <img
                src={
                  result?.school?.slug
                    ? `https://simple-storage-server.highereducation.com/${result?.school.slug}.png?w=130&h=80`
                    : undefined
                }
                alt={result?.school?.name}
              />
            </div>
            <div>
              <h4 className={styles.title}>{result?.school?.name}</h4>
              {result?.school?.city && result?.school?.state && (
                <div className={styles.location}>
                  <span className={styles.locationIcon}>
                    <HiLocationMarker size={16} />
                  </span>{' '}
                  {result?.school.city}, {result?.school.state}
                </div>
              )}
            </div>
          </div>
          <div className={isComparisonTest ? styles.ctaContainer : styles.ctaContainerDisplayContents}>
            {!isProgramSubmitted ? (
              <button
                data-testid={`recommender-popup-card-button-${position}`}
                type="button"
                className={styles.cardBtnCta}
              >
                Explore Program
              </button>
            ) : (
              <button
                data-testid={`recommender-popup-card-button-${position}`}
                type="button"
                className={styles.cardBtnCtaSubmitted}
              >
                {applicationCtaData?.label || 'Start Your Application'}
              </button>
            )}
            {isComparisonTest && <ComparisonCheckbox result={result} />}
          </div>
        </div>
        <div className={styles.cardBody}>
          <h3 className={styles.programName}>{result?.program?.name}</h3>
          <ProductFlags flags={result?.school?.additional} />
          <SchoolProgramTabs
            programSnippet={result?.program?.snippet}
            schoolAdditionalInfo={result?.school?.additional}
          />
        </div>
      </div>
    </section>
  );
};

// We use memo to prevent rerenders when props and state haven't changed
export default memo(RecommenderCard);
